/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import { IBritaxHoverModuleData } from './britax-hover-module.data';
import { IBritaxHoverModuleProps } from './britax-hover-module.props.autogenerated';
import { IModuleProps } from '@msdyn365-commerce-modules/utilities';
import * as Msdyn365 from '@msdyn365-commerce/core';
import { IGridSettings, IImageData, IImageSettings, Image, ICoreContext, IGeneric, IAny } from '@msdyn365-commerce/core';
import classnames from 'classnames';
export interface IBritaxHoverModuleState {
    [key: string]: boolean | undefined;
}
export interface IBritaxHoverModulePropsView extends IBritaxHoverModuleProps<IBritaxHoverModuleData> {
    hoveredCard?: React.ReactNode;
    AccountProfile?: IModuleProps;
}
export interface IBritaxHoverModuleViewProps extends IBritaxHoverModulePropsView {}

/**
 *
 * BritaxHoverModule component
 * @extends {React.PureComponent<IBritaxHoverModuleProps<IBritaxHoverModuleData>>}
 */
class BritaxHoverModule extends React.PureComponent<IBritaxHoverModuleProps<IBritaxHoverModuleData>, IBritaxHoverModuleState> {
    public constructor(props: IBritaxHoverModuleProps<IBritaxHoverModuleData>) {
        super(props);
        this.state = {};
    }

    private __renderResizedImage(
        imageSettings?: IImageSettings,
        gridSettings?: IGridSettings,
        imageUrl?: string,
        fallbackImageUrl?: string,
        altText?: string,
        context?: ICoreContext<IGeneric<IAny>>
    ): JSX.Element | null {
        const defaultImageSettings: Msdyn365.IImageSettings = {
            viewports: {
                xs: { q: 'w=295&h=295&m=6', w: 295, h: 295 },
                lg: { q: 'w=205&h=205&m=6', w: 205, h: 205 },
                sm: { q: 'w=205&h=205&m=8', w: 205, h: 205 },
                md: { q: 'w=205&h=205&m=8', w: 205, h: 205 }
            },
            lazyload: true,
            cropFocalRegion: true
        };
        const imageSetting = defaultImageSettings;
        //console.log(imageUrl);
        if (!imageUrl || !gridSettings || !imageSetting) {
            return null;
        }
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment -- Site level config can be of any type.
        const contextConfig = context?.app.config?.placeholderImageName;
        const emptyPlaceHolderImage = contextConfig as string;
        let fallbackImageSource = fallbackImageUrl;
        if (emptyPlaceHolderImage && fallbackImageUrl) {
            fallbackImageSource = `${fallbackImageUrl},${emptyPlaceHolderImage}`;
        }
        const img: IImageData = {
            src: imageUrl,
            altText: altText ? altText : '',
            fallBackSrc: fallbackImageSource
        };
        const imageProps = {
            gridSettings,
            imageSetting
        };
        imageProps.imageSetting.cropFocalRegion = true;
        return (
            <Image
                requestContext={context?.actionContext.requestContext}
                {...img}
                {...imageProps}
                loadFailureBehavior='empty'
                bypassHideOnFailure
            />
        );
    }

    /**
     * Renders the sign in button.
     * @param user - Request context user.
     * @param resources - Resources.
     * @returns React Node.
     */
    private _getCard(props: IBritaxHoverModulePropsView): React.ReactNode | undefined {
        const handleMouseEnter = (index: number) => {
            this.setState({ [`hovered${index}`]: true });
        };
        const handleMouseLeave = (index: number) => {
            this.setState({ [`hovered${index}`]: false });
        };
        const {
            image_1,
            hoveredImage_1,
            label_1,
            link_1,
            image_2,
            hoveredImage_2,
            label_2,
            link_2,
            image_3,
            hoveredImage_3,
            label_3,
            link_3,
            image_4,
            hoveredImage_4,
            label_4,
            link_4,
            image_5,
            hoveredImage_5,
            label_5,
            link_5,
            image_6,
            hoveredImage_6,
            label_6,
            link_6,
            moduleHeading,
            imageSettings,
            isNZBasedConfig
        } = props.config;
        const moduleClassName = 'ms-flip-cards';
        const hoverModuleSite = isNZBasedConfig;
        const moduleClassNameImageContainer = hoverModuleSite ? 'shop-by-container' : '';
        return (
            <>
                <h1 className={`${moduleClassName}__heading`}>{moduleHeading}</h1>
                <div className={classnames(moduleClassName, moduleClassNameImageContainer)}>
                    {image_1 && hoveredImage_1 && (
                        <div
                            className={`${moduleClassName}\__image ${hoverModuleSite ? 'shop-by' : ''}`}
                            onMouseEnter={() => handleMouseEnter(1)}
                            onMouseLeave={() => handleMouseLeave(1)}
                        >
                            <a href={link_1?.destinationUrl}>
                                <figure>
                                    {/* <img src={this.state[`hovered${1}`] ? hoveredImage_1.src : image_1.src} alt={label_1} /> */}
                                    {this.__renderResizedImage(
                                        imageSettings,
                                        props.context.request.gridSettings,
                                        this.state[`hovered${1}`] ? hoveredImage_1.src : image_1.src,
                                        undefined,
                                        label_1,
                                        props.context
                                    )}
                                    <figcaption className={`${moduleClassName}\__caption`}>{label_1 ?? ''}</figcaption>
                                </figure>
                            </a>
                        </div>
                    )}
                    {image_2 && hoveredImage_2 && (
                        <div
                            className={`${moduleClassName}\__image  ${hoverModuleSite ? 'shop-by' : ''}`}
                            onMouseEnter={() => handleMouseEnter(2)}
                            onMouseLeave={() => handleMouseLeave(2)}
                        >
                            <a href={link_2?.destinationUrl}>
                                <figure>
                                    {/* <img src={this.state[`hovered${2}`] ? hoveredImage_2.src : image_2.src} alt={label_2} /> */}
                                    {this.__renderResizedImage(
                                        imageSettings,
                                        props.context.request.gridSettings,
                                        this.state[`hovered${2}`] ? hoveredImage_2.src : image_2.src,
                                        undefined,
                                        label_2,
                                        props.context
                                    )}
                                    <figcaption className={`${moduleClassName}\__caption`}>{label_2 ?? ''}</figcaption>
                                </figure>
                            </a>
                        </div>
                    )}
                    {image_3 && hoveredImage_3 && (
                        <div
                            className={`${moduleClassName}\__image  ${hoverModuleSite ? 'shop-by' : ''}`}
                            onMouseEnter={() => handleMouseEnter(3)}
                            onMouseLeave={() => handleMouseLeave(3)}
                        >
                            <a href={link_3?.destinationUrl}>
                                <figure>
                                    {/* <img src={this.state[`hovered${3}`] ? hoveredImage_3.src : image_3.src} alt={label_3} /> */}
                                    {this.__renderResizedImage(
                                        imageSettings,
                                        props.context.request.gridSettings,
                                        this.state[`hovered${3}`] ? hoveredImage_3.src : image_3.src,
                                        undefined,
                                        label_3,
                                        props.context
                                    )}
                                    <figcaption className={`${moduleClassName}\__caption`}>{label_3 ?? ''}</figcaption>
                                </figure>
                            </a>
                        </div>
                    )}
                    {image_4 && hoveredImage_4 && (
                        <div
                            className={`${moduleClassName}\__image  ${hoverModuleSite ? 'shop-by' : ''}`}
                            onMouseEnter={() => handleMouseEnter(4)}
                            onMouseLeave={() => handleMouseLeave(4)}
                        >
                            <a href={link_4?.destinationUrl}>
                                <figure className={`${moduleClassName}\__image`}>
                                    {/* <img src={this.state[`hovered${4}`] ? hoveredImage_4.src : image_4.src} alt={label_4} /> */}
                                    {this.__renderResizedImage(
                                        imageSettings,
                                        props.context.request.gridSettings,
                                        this.state[`hovered${4}`] ? hoveredImage_4.src : image_4.src,
                                        undefined,
                                        label_4,
                                        props.context
                                    )}
                                    <figcaption className={`${moduleClassName}\__caption`}>{label_4 ?? ''}</figcaption>
                                </figure>
                            </a>
                        </div>
                    )}
                    {image_5 && hoveredImage_5 && (
                        <div
                            className={`${moduleClassName}\__image  ${hoverModuleSite ? 'shop-by' : ''}`}
                            onMouseEnter={() => handleMouseEnter(5)}
                            onMouseLeave={() => handleMouseLeave(5)}
                        >
                            <a href={link_5?.destinationUrl}>
                                <figure className={`${moduleClassName}\__image`}>
                                    {/* <img src={this.state[`hovered${5}`] ? hoveredImage_5.src : image_5.src} alt={label_5} /> */}
                                    {this.__renderResizedImage(
                                        imageSettings,
                                        props.context.request.gridSettings,
                                        this.state[`hovered${5}`] ? hoveredImage_5.src : image_5.src,
                                        undefined,
                                        label_5,
                                        props.context
                                    )}
                                    <figcaption className={`${moduleClassName}\__caption`}>{label_5 ?? ''}</figcaption>
                                </figure>
                            </a>
                        </div>
                    )}
                    {image_6 && hoveredImage_6 && (
                        <div
                            className={`${moduleClassName}\__image  ${hoverModuleSite ? 'shop-by' : ''}`}
                            onMouseEnter={() => handleMouseEnter(6)}
                            onMouseLeave={() => handleMouseLeave(6)}
                        >
                            <a href={link_6?.destinationUrl}>
                                <figure className={`${moduleClassName}\__image`}>
                                    {/* <img src={this.state[`hovered${6}`] ? hoveredImage_6.src : image_6.src} alt={label_6} /> */}
                                    {this.__renderResizedImage(
                                        imageSettings,
                                        props.context.request.gridSettings,
                                        this.state[`hovered${6}`] ? hoveredImage_6.src : image_6.src,
                                        undefined,
                                        label_5,
                                        props.context
                                    )}
                                    <figcaption className={`${moduleClassName}\__caption`}>{label_6 ?? ''}</figcaption>
                                </figure>
                            </a>
                        </div>
                    )}
                </div>
            </>
        );
    }
    public render(): JSX.Element | null {
        const viewprops: IBritaxHoverModulePropsView = {
            ...this.props,
            hoveredCard: this._getCard(this.props)
        };

        return this.props.renderView(viewprops);
    }
}

export default BritaxHoverModule;
